import React from "react";
import ExplainBoxplot from "views/Status/Subscribers/Help/BoxPlotDemo";

const _SYMBOLS_ = {
  MAX: { icon: "format_line_spacing" },
  DIRECTION: { icon: "import_export" },
  ATTRIBUTE: { icon: "folder_open" },
  GROUP: { icon: "group" },
  IP: { icon: "devices" },
  GEAR: { icon: "settings" },
  REFRESH: { icon: "refresh" },
  MAGNIFYING: { icon: "search" },
  DELETE: { icon: "delete" },
  SWAP: { icon: "swap_horiz" },
  RECONNECT: { icon: "swap_vert" },
  EDIT: { icon: "edit" },
  ADD: { icon: "add_circle" },
  MENU: { icon: "more_vert" },
  METRIC: { icon: "apps" },
  NET: { icon: "device_hub" },
  TIMESPAN: { icon: "crop_16_9" },
  FULL: { icon: "zoom_out_map" },
  DATE: { icon: "date_range" },
  CHIP: { icon: "memory" },
  TIMELINE: { icon: "timeline" },
  SPLIT: { icon: "call_split" },
  DOWN: { icon: "arrow_downward", className: "col-red" },
  UP: { icon: "arrow_upward", className: "col-green" },
  LABEL: { icon: "label_outline" },
  CLOUD: { icon: "cloud_queue" },
  LIST: { icon: "list" },
  PRINT: { icon: "print" },
  SUBS: { icon: "person" },
  MORE_VERT: { icon: "more_vert" },
  EXPLAINBOXPLOT: { explain: false, Component: ExplainBoxplot },
};

const pipe =
  (method, ...methods) =>
  (source, ...args) =>
    methods.reduce(
      (result, fn) => fn(result, ...args),
      method(source, ...args)
    );

const toDescription = ({ label, value }) => ({
  type: "description",
  label: consumeDescriptionContent(label),
  value: consumeDescriptionContent(value),
});

const whenNotToken = (method) => (source) =>
  typeof source === "object" ? source /*already a token*/ : method(source);

const mayBe = (regexp, tokenize) =>
  whenNotToken((source) =>
    ((match) => (match === null ? source : tokenize(match.groups, match)))(
      source.match(regexp)
    )
  );

const mayBeMany = (regexp, tokenize) =>
  whenNotToken((source) =>
    ((matches) => (matches.length === 0 ? source : tokenize(matches, matches)))(
      [...source.matchAll(regexp)]
    )
  );

const mayBeADescription = mayBe(
  /(?<label>.*?):\s+(?<value>.*)/,
  toDescription
);

const toLinkWithTexts = ({ pre, post, url, content = null }) => ({
  type: "link",
  url,
  content,
  pre: consumeLinkContent(pre),
  post: consumeLinkContent(post),
});

const linkRawRegexp =
  /^(?<pre>.*)?(?<url>(http|https):\/\/[^\s]*)(?<post>.*)$/s;
const linkContentRegexp =
  /^(?<pre>.*?)\[(?<content>[^\]]+)\]\((?<url>(http|https):\/\/[^)]+)\)(?<post>.*)$/s;

const mayBeALink = pipe(
  mayBe(linkContentRegexp, toLinkWithTexts),
  mayBe(linkRawRegexp, toLinkWithTexts)
);

const orText = (content) =>
  typeof content === "object" ? content : { type: "text", content };

const toMarkerWithTexts = ({ pre, post, symbol }) => ({
  type: "marker",
  ...(_SYMBOLS_[symbol] || {}),
  symbol,
  pre: consumeContent(pre),
  post: consumeContent(post),
});

const markerRegexp = /^(?<pre>.*?)\[(?<symbol>[^\]]+)\](?<post>.*)$/s;

const mayBeAMarker = mayBe(markerRegexp, toMarkerWithTexts);

const butlast = (arr, n = 1) => (n <= 0 ? arr : arr.slice(0, -n));

const last = (arr) => arr.at(-1);

const addToNestedItems = ({ items = [], ...target }, item) => ({
  ...target,
  items: [...items, item],
});

const tryToFit = (target, current) =>
  current.level > target.level
    ? [addToNestedItems(target, current)]
    : [target, current];

const intoItemsWithSubItems = (result, current) =>
  result && result.length > 0
    ? [...butlast(result), ...tryToFit(last(result), current)]
    : [current];

export const nestifyItems = (items) => items.reduce(intoItemsWithSubItems, []);

const INDENT_SPACES_PER_LEVEL = 2;

const leadingTextBefore = (matches) => {
  const first = matches[0];
  const text = first.input.slice(0, first.index);
  return hasText(text) ? consumeLeadLineContent(text) : null;
};

const toListTokens = ([...items]) => ({
  type: "list",
  pre: leadingTextBefore(items),
  items: nestifyItems(
    items.map(({ groups, index }) => ({
      type: "item",
      level: Math.floor(groups.indent.length / INDENT_SPACES_PER_LEVEL) + 1,
      content: consumeContent(groups.term),
      index,
    }))
  ),
});

const mayBeAList = mayBeMany(
  /^(?<indent> *)-\s?(?<term>.*)$/gm,
  toListTokens
);

const toHeadingToken = ({ text, heading }) => ({
  type: "heading",
  level: heading.length,
  content: consumeLeadLineText(text),
});

const mayBeAHeading = mayBe(
  /^(?<heading>#+)\s?(?<text>[^\r\n]*)$/,
  toHeadingToken
);

const toLeadLineToken = ({ text, post, bold = false }) => ({
  type: "lead-line",
  text: consumeLeadLineText(text),
  bold,
  post: post && consumeLeadLineContent(post),
});

const toBoldToken = ({ content, post, pre }) => ({
  type: "text",
  bold: true,
  content,
  pre: pre && consumeLeadLineContent(pre),
  post: post && consumeLeadLineContent(post),
});

const mayBeBold = mayBe(
  /^(?<pre>.*?)\*\*(?<content>[^*]+)\*\*(?<post>.*)$/s,
  toBoldToken
);

const hasText = (source) => source && source.trim().length > 0;

const toCodeToken = ({ content, language, pre, post }) => ({
  type: "code",
  language,
  content,
  pre: hasText(pre) ? consumeLeadLineContent(pre) : null,
  post: hasText(post) ? consumeLeadLineContent(post) : null,
});

const mayBeCode = mayBe(
  /^(?<pre>[\s\S]*?)```(?:(?<language>[a-zA-Z0-9_-]*)\s*\r?\n)?(?<content>.*?)\r?\n?```(?<post>[\s\S]*)$/s,
  toCodeToken
);

//Lead lines are the first line of a block, can be followed by lists, links, or more text.
//A line holding a list item (optionally indented) is not a lead line.
const mayBeALeadLine = mayBe(
  /^(?<text>(?! *-)[^\r\n]+)(?:\r?\n(?<post>[\s\S]*))?$/,
  toLeadLineToken
);

const consumeContent = pipe(
  mayBeADescription,
  mayBeALink,
  mayBeAMarker,
  mayBeBold,
  mayBeCode,
  orText
);

const consumeDescriptionContent = pipe(mayBeALink, mayBeAMarker, mayBeBold, orText);

const consumeLinkContent = pipe(mayBeAMarker, mayBeBold, orText);

const consumeParagraphBlock = pipe(
  mayBeAHeading,
  mayBeALeadLine,
  mayBeAList,
  mayBeCode,
  orText
);

const consumeLeadLineText = pipe(
  mayBeALink,
  mayBeAMarker,
  mayBeBold,
  mayBeCode,
  orText
);

const consumeLeadLineContent = pipe(
  mayBeAList,
  mayBeCode,
  mayBeAMarker,
  mayBeALink,
  mayBeBold,
  orText
);

const tokenizeRootBlock = (source) =>
  source.split(/\r?\n\r?\n/).flatMap(consumeParagraphBlock);

const listContent = ({ items = [] }) =>
  items.map((item, index) => (
    <li key={item.index ?? index}>
      {intoComponents(item.content)}
      {item.items ? <ul>{listContent(item)}</ul> : null}
    </li>
  ));

const renderList = (token, index) =>
  token.items && token.items.length > 0 ? (
    <React.Fragment key={index}>
      {(token.pre && intoComponents(token.pre)) || null}
      <ul>{listContent(token)}</ul>
    </React.Fragment>
  ) : null;

const renderMarker = ({ pre, post, icon, Component, symbol, ...token }, index) => (
  <React.Fragment key={index}>
    {intoComponents(pre)}
    {icon ? renderIconMarker({ icon, symbol, ...token }) : null}
    {Component ? <Component /> : null}
    {!icon && !Component ? `[${symbol}]` : null}
    {intoComponents(post)}
  </React.Fragment>
);

const renderLink = ({ pre, post, url, content }, index) => (
  <React.Fragment key={index}>
    {intoComponents(pre)}
    <a href={url} target="_blank" rel="noopener noreferrer">{content === null ? url : content}</a>
    {intoComponents(post)}
  </React.Fragment>
);

const renderIconMarker = ({ icon, className = "", symbol, explain = true }) => (
  <>
    <i className={`material-icons valign-icon ${className}`.trim()}>{icon}</i>
    {explain === true ? `(${symbol})` : null}
  </>
);

const renderDescriptionMarkerLabel = ({ pre, post, symbol, ...token }) => (
  <>
    {intoComponents(pre)}
    {renderIconMarker({ ...token, explain: false })}
    <b>
      {`(${symbol})`}
      {intoComponents(post)}
    </b>
  </>
);

const renderDescriptionLabel = (token) =>
  token.type === "marker" ? (
    renderDescriptionMarkerLabel(token)
  ) : token.type === "text" && token.bold === true ? (
    // Label is already emphasized as a whole; avoid a redundant nested <b>.
    <b>{intoComponents({ ...token, bold: false })}</b>
  ) : (
    <b>{intoComponents(token)}</b>
  );

const isPlainText = ({ type, bold = false, pre, post }) =>
  type === "text" && bold === false && !pre && !post;

const renderDescriptionValue = ({ type, content, ...token }) =>
  isPlainText({ type, content, ...token }) ? (
    ": " + content
  ) : (
    <>
      {": "}
      {intoComponents({ type, content, ...token })}
    </>
  );

const renderDescription = (token, index) => (
  <React.Fragment key={index}>
    {renderDescriptionLabel(token.label)}
    {renderDescriptionValue(token.value)}
  </React.Fragment>
);

const renderLeadLine = ({ text, bold = false, post }, index) => (
  <React.Fragment key={index}>
    <p>
      {bold === true ? <b>{intoComponents(text)}</b> : intoComponents(text)}
    </p>
    {(post && intoComponents(post)) || null}
  </React.Fragment>
);
const renderHeading = ({ content, level }, index) =>
  level === 3 ? (
    <h5 key={index}>{intoComponents(content)}</h5>
  ) : (
    <React.Fragment key={index}>{intoComponents(content)}</React.Fragment>
  );

const renderText = ({ content, pre, post, bold = false }, index) => (
  <React.Fragment key={index}>
    {(pre && intoComponents(pre)) || null}
    {bold === true ? (
      <b>{content}</b>
    ) : (
      <React.Fragment>{content}</React.Fragment>
    )}
    {(post && intoComponents(post)) || null}
  </React.Fragment>
);

const renderCode = ({ content, pre, post }, index) => (
  <React.Fragment key={index}>
    {(pre && intoComponents(pre)) || null}
    <div className="text-code">
      {content}
    </div>
    {(post && intoComponents(post)) || null}
  </React.Fragment>
);

const intoComponents = ({ type, ...token }, index) =>
  type === "description" ? (
    renderDescription(token, index)
  ) : type === "marker" ? (
    renderMarker(token, index)
  ) : type === "heading" ? (
    renderHeading(token, index)
  ) : type === "link" ? (
    renderLink(token, index)
  ) : type === "list" ? (
    renderList(token, index)
  ) : type === "lead-line" ? (
    renderLeadLine(token, index)
  ) : type === "text" ? (
    renderText(token, index)
  ) : type === "code" ? (
    renderCode(token, index)
  ) : type === "paragraph" ? (
    <p key={index}>{intoComponents(token.content)}</p>
  ) : (
    console.error("token type not recognized", type, token) || null
  );

export const render = (source) => (
  <React.Fragment>
    {tokenizeRootBlock(source).map(intoComponents)}
  </React.Fragment>
);

export default render;
